import { useEffect, useRef } from 'react';
import { Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCallOptional } from '@/providers/call-context';

function VideoTile({
  stream,
  muted,
  mirror,
  className,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  mirror?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = stream;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={`${className || ''} ${mirror ? 'scale-x-[-1]' : ''}`.trim()}
    />
  );
}

export function CallOverlay() {
  const callApi = useCallOptional();
  if (!callApi || callApi.phase === 'idle' || !callApi.call) return null;

  const { phase, call, localStream, remoteStream, muted, cameraOff } = callApi;
  const peerName = call.peer?.name || 'User';
  const peerInitials = peerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('');

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-slate-950 text-white">
      <div className="relative flex-1 overflow-hidden">
        {remoteStream ? (
          <VideoTile stream={remoteStream} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4 bg-gradient-to-b from-slate-900 to-slate-950">
            <Avatar className="h-28 w-28 border-2 border-white/20">
              <AvatarImage src={call.peer?.image || undefined} alt={peerName} />
              <AvatarFallback className="bg-primary/30 text-3xl font-bold text-white">
                {peerInitials || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="text-center">
              <p className="text-2xl font-semibold tracking-tight">{peerName}</p>
              <p className="mt-1 text-sm text-white/70">
                {phase === 'outgoing' && 'Calling…'}
                {phase === 'incoming' && 'Incoming call — Accept will ask for camera & mic'}
                {phase === 'active' && 'Connecting…'}
              </p>
            </div>
          </div>
        )}

        {localStream ? (
          <div className="absolute bottom-28 right-4 h-36 w-28 overflow-hidden rounded-2xl border border-white/20 bg-black shadow-xl sm:h-44 sm:w-32">
            <VideoTile
              stream={localStream}
              muted
              mirror
              className={`h-full w-full object-cover ${cameraOff ? 'opacity-0' : ''}`}
            />
            {cameraOff ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-xs text-white/70">
                Camera off
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-center gap-4 px-6 pb-10 pt-4">
        {phase === 'incoming' ? (
          <>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={() => void callApi.rejectCall()}
              aria-label="Decline"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
            <Button
              size="lg"
              className="h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600"
              onClick={() => void callApi.acceptCall()}
              aria-label="Accept"
            >
              <Phone className="h-6 w-6" />
            </Button>
          </>
        ) : (
          <>
            <Button
              size="lg"
              variant="secondary"
              className="h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20"
              onClick={callApi.toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant="secondary"
              className="h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20"
              onClick={callApi.toggleCamera}
              aria-label={cameraOff ? 'Camera on' : 'Camera off'}
            >
              {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={() => void callApi.endCall()}
              aria-label="End call"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
